const BACK_IN_STOCK_TARGET_OVERRIDE_STORAGE_KEY = 'weka_back_in_stock_target_overrides_v1';
const BACK_IN_STOCK_TARGET_CHANGE_EVENT = 'weka:back-in-stock-targets-changed';

type BackInStockTargetOverrideState = 'subscribed' | 'unsubscribed';

const sanitizeOverrideState = (value: unknown): BackInStockTargetOverrideState | null => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'subscribed' || normalized === 'unsubscribed') {
        return normalized;
    }

    return null;
};

export const sanitizeBackInStockTargetKeys = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((entry) => String(entry || '').trim())
        .filter((entry) => entry !== '');
};

const readBackInStockTargetOverrides = (): Record<string, BackInStockTargetOverrideState> => {
    if (typeof window === 'undefined') {
        return {};
    }

    try {
        const raw = window.sessionStorage.getItem(BACK_IN_STOCK_TARGET_OVERRIDE_STORAGE_KEY);
        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }

        const overrides: Record<string, BackInStockTargetOverrideState> = {};
        Object.entries(parsed as Record<string, unknown>).forEach(([targetKey, state]) => {
            const normalizedTargetKey = String(targetKey || '').trim();
            const normalizedState = sanitizeOverrideState(state);
            if (normalizedTargetKey === '' || normalizedState === null) {
                return;
            }

            overrides[normalizedTargetKey] = normalizedState;
        });

        return overrides;
    } catch {
        return {};
    }
};

const persistBackInStockTargetOverrides = (overrides: Record<string, BackInStockTargetOverrideState>): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.sessionStorage.setItem(BACK_IN_STOCK_TARGET_OVERRIDE_STORAGE_KEY, JSON.stringify(overrides));
    } catch {
        // Ignore storage write failures.
    }
};

const emitBackInStockTargetChange = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new CustomEvent(BACK_IN_STOCK_TARGET_CHANGE_EVENT));
};

export const resolveBackInStockTargetKeys = (baseTargetKeys: unknown): string[] => {
    const resolvedTargets = new Set(sanitizeBackInStockTargetKeys(baseTargetKeys));
    const overrides = readBackInStockTargetOverrides();

    Object.entries(overrides).forEach(([targetKey, state]) => {
        if (state === 'subscribed') {
            resolvedTargets.add(targetKey);
            return;
        }

        resolvedTargets.delete(targetKey);
    });

    return Array.from(resolvedTargets);
};

export const setBackInStockTargetSubscriptionState = (targetKey: string, isSubscribed: boolean): void => {
    const normalizedTargetKey = String(targetKey || '').trim();
    if (normalizedTargetKey === '') {
        return;
    }

    const overrides = readBackInStockTargetOverrides();
    overrides[normalizedTargetKey] = isSubscribed ? 'subscribed' : 'unsubscribed';
    persistBackInStockTargetOverrides(overrides);
    emitBackInStockTargetChange();
};

export const onBackInStockTargetStateChange = (callback: () => void): (() => void) => {
    if (typeof window === 'undefined') {
        return () => undefined;
    }

    const handleChange = () => {
        callback();
    };

    window.addEventListener(BACK_IN_STOCK_TARGET_CHANGE_EVENT, handleChange);

    return () => {
        window.removeEventListener(BACK_IN_STOCK_TARGET_CHANGE_EVENT, handleChange);
    };
};
